import { useMutation, useQuery } from "@apollo/client";
import { VideoCameraIcon } from "@heroicons/react/24/outline";
import {
  HandThumbDownIcon,
  HandThumbUpIcon,
  PlayIcon,
  UserIcon,
} from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import ActionButton from "../../components/ActionButton/ActionButton";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Loader from "../../components/Loader/Loader";
import Page from "../../components/Page/Page";
import Badge from "../../components/Styled/Badge";
import { Row } from "../../components/Styled/Container";
import {
  MediaActionRow,
  MediaActions,
  MediaCard,
  MediaCornerActions,
  MediaFrame,
  MediaGrid,
  MediaIconChip,
  MediaImage,
  MediaTop,
} from "../../components/Styled/Media";
import { User } from "../../graphql/graphql";
import { APPROVE_MATCH_VIDEO } from "../../graphql/mutations-gql";
import { MATCH_VIDEOS_APPROVAL } from "../../graphql/queries-gql";

const MatchVideos = () => {
  const { data, loading, error } = useQuery(MATCH_VIDEOS_APPROVAL);
  const [approveMatchVideo] = useMutation(APPROVE_MATCH_VIDEO);
  const navigate = useNavigate();

  const users: User[] = data?.matchVideosApproval || [];

  const handleApprove = (value: boolean, userId: string) => {
    approveMatchVideo({
      variables: {
        userId,
        value,
      },
      onCompleted: (data) => {
        console.log("approveMatchVideo onCompleted", data);
      },
      onError: (error) => {
        console.log("approveMatchVideo onError", error);
      },
      refetchQueries: [MATCH_VIDEOS_APPROVAL],
    });
  };

  const showVideo = (url: string) => {
    window.open(url, "_blank");
  };

  return (
    <Page
      title="Match videos"
      description="Hover a video to approve or reject it, click to play."
      badge={
        users.length > 0 && <Badge $tone="primary">{users.length} waiting</Badge>
      }
    >
      {loading && (
        <Row justifyContent="center">
          <Loader />
        </Row>
      )}
      {error && <ErrorAlert error={error} />}

      {!loading && !error && users.length === 0 && (
        <EmptyState
          Icon={VideoCameraIcon}
          title="No match videos to review"
          description="New match videos show up here."
        />
      )}

      {users.length > 0 && (
        <MediaGrid $min={180} $gap={12}>
          {users.map((user) => {
            return (
              <MediaCard key={user._id}>
                <MediaFrame
                  onClick={() => showVideo(user.matchVideo?.url ?? "")}
                >
                  <MediaImage
                    src={user.matchVideo?.thumbnailUrl ?? ""}
                    alt=""
                    loading="lazy"
                  />
                  <MediaTop>
                    <span />
                    <MediaIconChip title="Video">
                      <PlayIcon width={13} />
                    </MediaIconChip>
                  </MediaTop>
                  <MediaCornerActions>
                    <ActionButton
                      label="Open user"
                      size={30}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/user/${user._id}`);
                      }}
                    >
                      <UserIcon />
                    </ActionButton>
                  </MediaCornerActions>
                  <MediaActions>
                    <MediaActionRow>
                      <ActionButton
                        label="Approve"
                        tone="approve"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(true, user._id);
                        }}
                      >
                        <HandThumbUpIcon />
                      </ActionButton>
                      <ActionButton
                        label="Reject"
                        tone="reject"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(false, user._id);
                        }}
                      >
                        <HandThumbDownIcon />
                      </ActionButton>
                    </MediaActionRow>
                  </MediaActions>
                </MediaFrame>
              </MediaCard>
            );
          })}
        </MediaGrid>
      )}
    </Page>
  );
};
export default MatchVideos;
